import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  FilterList as FilterIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { formatCurrency } from '../utils/currency';
import { Product } from '../types';
import { useGetProductsQuery, useGenerateUniqueBarcodeMutation, useValidateBarcodeQuery, useCreateProductMutation, useUpdateProductMutation } from '../services/products.service';
import { useGetLocationsQuery } from '../services/locations.service';

interface ProductFormData {
  name: string;
  description?: string;
  code?: string;
  sku?: string;
  barcode?: string;
  alternativeBarcodes?: string[]; // Multiple alternative barcodes/keyphrases
  category: string;
  subcategory?: string;
  unit: string;
  costPrice: number;
  salePrice: number;
  // Discount prices for different customer levels
  discountPrice1?: number;
  discountPrice2?: number;
  discountPrice3?: number;
  discountPrice4?: number;
  minStock: number;
  // Campos de inventario inicial
  locationId: string;
  initialQuantity: number;
}

const categories = [
  'VARIOS',
  'VARIOOOOOOOOOOOOS',
  'COCINA',
  'PLÁSTICO GENERAL',
  'JUGUETERIA',
  'HOGAR',
  'FLORISTERIA',
  'CRISTALERIA',
  'NAVIDEÑO',
  'JARDINERIA',
  'DESECHABLE',
  'ACCESORIOS',
  'ELECTRODOMESTICOS',
  'VERANO',
  'MELAMINA',
  'BEBE',
  'ESCOLAR',
  'PIÑATERIA',
  'MASCOTA',
  'BOLSAS PLASTICAS',
  'COSMETICOS',
  'GRUPO',
  'IMAGENES',
  'MATERIAL CHINO',
  'NAYLON',
  'PJ1',
  'OTROS'
];

const ProductsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [stockFilter, setStockFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [openBarcodeDialog, setOpenBarcodeDialog] = useState(false);
  const [selectedProductBarcode, setSelectedProductBarcode] = useState<Product | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>(categories);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedCategory, stockFilter]);

  // Reset categories to predefined list on component mount
  useEffect(() => {
    setAvailableCategories(categories);
  }, []);

  // Obtener productos de la API con filtros y paginación
  const { 
    data: productsResponse, 
    isLoading, 
    error, 
    refetch 
  } = useGetProductsQuery({
    page: currentPage,
    limit: itemsPerPage,
    search: debouncedSearchTerm.length >= 2 ? debouncedSearchTerm : undefined,
    category: selectedCategory || undefined,
    active: true,
  }, {
    skip: !debouncedSearchTerm && !selectedCategory && stockFilter === 'all'
  });

  // Obtener ubicaciones para el selector
  const { data: locationsResponse } = useGetLocationsQuery({ active: true });
  const locations = locationsResponse?.data || [];
  
  // Buscar "Almacén Principal" como ubicación por defecto
  const defaultLocation = locations.find(loc => loc.name.toLowerCase().includes('almacen principal') || loc.name.toLowerCase().includes('almacén principal')) || locations[0];

  const { 
    register, 
    handleSubmit, 
    reset, 
    control,
    formState: { errors },
    setValue,
    getValues,
    watch 
  } = useForm<ProductFormData>({
    defaultValues: {
      name: '',
      description: '',
      code: '',
      sku: '',
      barcode: '',
      category: '',
      subcategory: '',
      unit: '',
      costPrice: 0,
      salePrice: 0,
      discountPrice1: 0,
      discountPrice2: 0,
      discountPrice3: 0,
      discountPrice4: 0,
      minStock: 0,
      locationId: '',
      initialQuantity: 0,
    },
  });
  
  // Mutaciones para gestión de productos
  const [generateUniqueBarcode, { isLoading: generateBarcodeLoading }] = useGenerateUniqueBarcodeMutation();
  const [createProduct, { isLoading: creatingProduct }] = useCreateProductMutation();
  const [updateProduct, { isLoading: updatingProduct }] = useUpdateProductMutation();
  
  // Vigilar el código de barras para validación en tiempo real
  const watchedBarcode = watch('barcode');
  
  // Hook para validar código de barras (solo si hay un valor)
  const { data: barcodeValidation, isFetching: validatingBarcode } = useValidateBarcodeQuery(
    { 
      barcode: watchedBarcode || '', 
      excludeId: editingProduct?.id 
    },
    { 
      skip: !watchedBarcode || watchedBarcode.length < 8,
      refetchOnMountOrArgChange: true 
    }
  );

  // State for alternative codes validation
  const [alternativeCodesInput, setAlternativeCodesInput] = useState('');
  const [alternativeCodesError, setAlternativeCodesError] = useState('');
  
  const handleGenerateBarcode = async () => {
    try {
      const category = getValues('category') || 'Otros';
      // Limpiar el campo primero
      setValue('barcode', '', { shouldDirty: true, shouldTouch: true });
      
      const result = await generateUniqueBarcode({ category }).unwrap();
      setValue('barcode', result.data.barcode, { shouldDirty: true, shouldTouch: true });
      console.log(`Código generado en ${result.data.attempts} intentos`);
    } catch (error) {
      console.error('Error al generar código de barras:', error);
      alert('Error al generar código de barras. Por favor intenta nuevamente.');
    }
  };

  const products = productsResponse?.products || [];
  
  // Filter products based on stock
  const filteredProducts = products.filter(product => {
    const totalStock = product.inventories?.reduce((sum, inv) => sum + inv.quantity, 0) || 0;
    
    if (stockFilter === 'low') {
      return totalStock <= product.minStock;
    } else if (stockFilter === 'out') {
      return totalStock === 0;
    } else if (stockFilter === 'available') {
      return totalStock > product.minStock;
    }
    return true;
  });

  const totalStock = (product: Product) => {
    return product.inventories?.reduce((sum, inv) => sum + inv.quantity, 0) || 0;
  };

  const getStockStatus = (product: Product) => {
    const total = totalStock(product);
    if (total === 0) return { label: 'Agotado', color: 'error' as const };
    if (total <= product.minStock) return { label: 'Bajo', color: 'warning' as const };
    return { label: 'Disponible', color: 'success' as const };
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      
      // For editing, find the inventory quantity for the default location
      const defaultLocationInventory = product.inventories?.find(inv => inv.locationId === defaultLocation?.id);
      const currentQuantity = defaultLocationInventory?.quantity || 0;
      
      // Handle alternative codes - convert from comma-separated string to array
      const altCodes = product.alternativeCode ?
        product.alternativeCode.split(',').map(s => s.trim()).filter(s => s) : [];

      reset({
        name: product.name,
        description: product.description || '',
        code: product.code,
        sku: product.sku,
        barcode: product.barcode || '',
        alternativeBarcodes: altCodes, // Add alternative codes to form
        category: product.category,
        subcategory: product.subcategory || '',
        unit: product.unit,
        costPrice: product.costPrice,
        salePrice: product.salePrice,
        discountPrice1: product.discountPrice1 || 0,
        discountPrice2: product.discountPrice2 || 0,
        discountPrice3: product.discountPrice3 || 0,
        discountPrice4: product.discountPrice4 || 0,
        minStock: product.minStock,
        locationId: defaultLocation?.id || '',
        initialQuantity: currentQuantity, // Set current inventory quantity
      });

      // Also update the input field display
      setAlternativeCodesInput(product.alternativeCode || '');
    } else {
      setEditingProduct(null);
      setAlternativeCodesInput(''); // Clear input for new product
      reset({
        name: '',
        description: '',
        code: '',
        sku: '',
        barcode: '',
        category: '',
        subcategory: '',
        unit: 'UDS', // Default unit value
        costPrice: 0,
        salePrice: 0,
        discountPrice1: 0,
        discountPrice2: 0,
        discountPrice3: 0,
        discountPrice4: 0,
        minStock: 0,
        locationId: defaultLocation?.id || '',
        initialQuantity: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    reset();
  };

  const handleShowBarcode = (product: Product) => {
    setSelectedProductBarcode(product);
    setOpenBarcodeDialog(true);
  };

  const onSubmit = async (data: ProductFormData) => {
    // Check for alternative codes validation errors before submitting
    if (alternativeCodesError) {
      console.error('Cannot submit: Alternative codes validation error');
      return;
    }
    
    try {
      if (editingProduct) {
        // Actualizar producto existente
        await updateProduct({
          id: editingProduct.id,
          data: {
            ...data,
            // Convertir campos numéricos
            costPrice: Number(data.costPrice),
            salePrice: Number(data.salePrice),
            discountPrice1: data.discountPrice1 ? Number(data.discountPrice1) : undefined,
            discountPrice2: data.discountPrice2 ? Number(data.discountPrice2) : undefined,
            discountPrice3: data.discountPrice3 ? Number(data.discountPrice3) : undefined,
            discountPrice4: data.discountPrice4 ? Number(data.discountPrice4) : undefined,
            minStock: Number(data.minStock),
            // Convert alternativeBarcodes array to comma-separated string for legacy support
            alternativeCode: data.alternativeBarcodes?.length ? data.alternativeBarcodes.join(', ') : undefined,
            alternativeBarcodes: data.alternativeBarcodes || []
          }
        }).unwrap();
        console.log('Producto actualizado exitosamente');
      } else {
        // Crear nuevo producto
        await createProduct({
          ...data,
          // Solo enviar código si realmente se proporcionó uno
          ...(data.code && data.code.trim() !== '' && { code: data.code.trim() }),
          // Convertir campos numéricos
          costPrice: Number(data.costPrice),
          salePrice: Number(data.salePrice),
          discountPrice1: data.discountPrice1 ? Number(data.discountPrice1) : undefined,
          discountPrice2: data.discountPrice2 ? Number(data.discountPrice2) : undefined,
          discountPrice3: data.discountPrice3 ? Number(data.discountPrice3) : undefined,
          discountPrice4: data.discountPrice4 ? Number(data.discountPrice4) : undefined,
          minStock: Number(data.minStock),
          initialQuantity: Number(data.initialQuantity),
          isActive: true,
          taxRate: 15, // Valor por defecto para Honduras
          // Convert alternativeBarcodes array to comma-separated string for legacy support
          alternativeCode: data.alternativeBarcodes?.length ? data.alternativeBarcodes.join(', ') : undefined,
          alternativeBarcodes: data.alternativeBarcodes || []
        }).unwrap();
        console.log('Producto creado exitosamente');
      }
      
      // Cerrar dialog y limpiar formulario
      handleCloseDialog();
      
      // No necesitamos refetch porque RTK Query actualiza automáticamente con invalidatesTags
      
    } catch (error: any) {
      console.error('Error al guardar producto:', error);
      
      // Extraer el mensaje de error específico
      let errorMessage = editingProduct ? 'Error al actualizar el producto' : 'Error al crear el producto';
      
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  };

  const shouldShowProducts = debouncedSearchTerm || selectedCategory || stockFilter !== 'all';

  if (isLoading && shouldShowProducts) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar los productos. {error && 'data' in error ? String(error.data) : 'Error desconocido'}
        </Alert>
        <Button variant="contained" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Gestión de Productos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="large"
        >
          Nuevo Producto
        </Button>
      </Box>

      {/* Filtros y búsqueda */}
      <Paper sx={{ p: 3, mb: 3, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, SKU, código o código alternativo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Categoría"
              >
                <MenuItem value="">Todas</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Estado de Stock</InputLabel>
              <Select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                label="Estado de Stock"
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="available">Disponible</MenuItem>
                <MenuItem value="low">Stock Bajo</MenuItem>
                <MenuItem value="out">Agotado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              fullWidth
            >
              <ToggleButton value="grid">
                <GridViewIcon />
              </ToggleButton>
              <ToggleButton value="list">
                <ListViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        
        {shouldShowProducts && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredProducts.length} productos encontrados
            </Typography>
            <Button
              size="small"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                setStockFilter('all');
                setCurrentPage(1);
              }}
            >
              Limpiar filtros
            </Button>
          </Box>
        )}
      </Paper>

      {/* Scrollable content area */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto', 
        maxHeight: 'calc(100vh - 200px)', 
        paddingRight: '8px' 
      }}>
        {/* Mensaje cuando no hay filtros aplicados */}
        {!shouldShowProducts ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FilterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Utiliza los filtros para buscar productos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Busca por nombre, selecciona una categoría o filtra por estado de stock
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Lista de productos */}
          {viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {filteredProducts.map((product) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" component="div" fontWeight="bold">
                          {product.name}
                        </Typography>
                        <Chip label={product.category} size="small" color="primary" variant="outlined" />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {product.description}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          SKU: {product.sku}
                        </Typography>
                        {product.barcode && (
                          <Typography variant="body2" color="text.secondary">
                            Código: {product.barcode}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Unidad: {product.unit}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Precio:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {formatCurrency(product.salePrice)}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      {/* Información de inventario */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="bold">
                          Stock Total:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {totalStock(product)} {product.unit}
                          </Typography>
                          <Chip 
                            size="small" 
                            label={getStockStatus(product).label} 
                            color={getStockStatus(product).color}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                    
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        startIcon={<QrCodeIcon />}
                        onClick={() => handleShowBarcode(product)}
                        disabled={!product.barcode}
                      >
                        Código
                      </Button>
                      <Box>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenDialog(product)}
                          sx={{ mr: 1 }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => console.log('Eliminar producto')}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            // Vista de lista - más compacta
            <Paper>
              {filteredProducts.map((product, index) => (
                <Box key={product.id}>
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        SKU: {product.sku} | Código: {product.barcode || 'N/A'} | {product.category}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {formatCurrency(product.salePrice)}
                        </Typography>
                        <Typography variant="body2">
                          Stock: {totalStock(product)} {product.unit}
                        </Typography>
                      </Box>
                      <Chip 
                        size="small" 
                        label={getStockStatus(product).label} 
                        color={getStockStatus(product).color}
                      />
                      <Box>
                        <Button
                          size="small"
                          onClick={() => handleShowBarcode(product)}
                          disabled={!product.barcode}
                        >
                          <QrCodeIcon />
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <EditIcon />
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                  {index < filteredProducts.length - 1 && <Divider />}
                </Box>
              ))}
            </Paper>
          )}

          {filteredProducts.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No se encontraron productos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Intenta con otros filtros de búsqueda
              </Typography>
            </Paper>
          )}

          {/* Pagination */}
          {productsResponse?.pagination && productsResponse.pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Página {productsResponse.pagination.page} de {productsResponse.pagination.totalPages} 
                  ({productsResponse.pagination.total} productos)
                </Typography>
                <Pagination
                  count={productsResponse.pagination.totalPages}
                  page={currentPage}
                  onChange={(_, page) => setCurrentPage(page)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <InputLabel>Por página</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Por página"
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <MenuItem value={6}>6</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                    <MenuItem value={24}>24</MenuItem>
                    <MenuItem value={48}>48</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
          )}
        </>
      )}
      </Box> {/* End scrollable content area */}

      {/* Dialog para mostrar código de barras */}
      <Dialog open={openBarcodeDialog} onClose={() => setOpenBarcodeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Código de Barras</DialogTitle>
        <DialogContent>
          {selectedProductBarcode && (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {selectedProductBarcode.name}
              </Typography>
              <Box sx={{ 
                p: 3, 
                backgroundColor: 'background.paper',
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2
              }}>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                  {selectedProductBarcode.barcode}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                SKU: {selectedProductBarcode.sku}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBarcodeDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para crear/editar producto (mantener el existente) */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={3}>
                {/* Información básica */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('name', { required: 'El nombre es obligatorio' })}
                    fullWidth
                    label="Nombre del Producto"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('sku')}
                    fullWidth
                    label="SKU (Opcional)"
                    error={!!errors.sku}
                    helperText={errors.sku?.message || 'Se generará automáticamente si se deja vacío'}
                    placeholder="Ej: PLT-001"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('code')}
                    fullWidth
                    label="Código del Producto (Opcional)"
                    error={!!errors.code}
                    helperText={errors.code?.message || 'Se puede dejar vacío, se generará automáticamente'}
                    placeholder="Ej: 001-PRD (opcional)"
                  />
                </Grid>

                {/* Código de barras */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      {...register('barcode', {
                        validate: (value) => {
                          if (!value) return true; // Opcional
                          if (value.length < 8) return 'El código debe tener al menos 8 dígitos';
                          if (barcodeValidation && !barcodeValidation.data.isUnique) {
                            return `Este código ya existe en: ${barcodeValidation.data.existingProduct?.name}`;
                          }
                          return true;
                        }
                      })}
                      fullWidth
                      label="Código de Barras"
                      error={!!errors.barcode || (barcodeValidation && !barcodeValidation.data.isUnique)}
                      helperText={
                        errors.barcode?.message ||
                        (validatingBarcode && watchedBarcode && watchedBarcode.length >= 8 ? '🔍 Validando...' : '') ||
                        (barcodeValidation && watchedBarcode && watchedBarcode.length >= 8 ? 
                          (barcodeValidation.data.isUnique ? 
                            '✓ Código disponible' : 
                            `⚠️ Ya existe en: ${barcodeValidation.data.existingProduct?.name}`
                          ) : 
                          'Ingresa un código existente o genera uno nuevo'
                        )
                      }
                      placeholder="Ej: 7501234567890"
                      InputProps={{
                        endAdornment: validatingBarcode && watchedBarcode && watchedBarcode.length >= 8 ? (
                          <CircularProgress size={20} />
                        ) : barcodeValidation && watchedBarcode && watchedBarcode.length >= 8 ? (
                          barcodeValidation.data.isUnique ? (
                            <Typography color="success.main">✓</Typography>
                          ) : (
                            <Typography color="error.main">⚠️</Typography>
                          )
                        ) : null
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleGenerateBarcode}
                      sx={{ minWidth: 'auto', px: 2 }}
                      disabled={generateBarcodeLoading}
                    >
                      {generateBarcodeLoading ? 'Generando...' : 'Generar'}
                    </Button>
                  </Box>
                </Grid>

                {/* Códigos Alternativos */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Códigos Alternativos"
                    fullWidth
                    placeholder="Ej: jumbona, 220, cg (separados por comas)"
                    helperText={
                      alternativeCodesError || 
                      "Códigos o palabras clave que también identifiquen este producto. Separar con comas."
                    }
                    error={!!alternativeCodesError}
                    onChange={(e) => {
                      const value = e.target.value;
                      const alternatives = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];
                      
                      // Clear previous error
                      setAlternativeCodesError('');
                      setAlternativeCodesInput(value);
                      
                      // Update form data
                      setValue('alternativeBarcodes', alternatives);
                      
                      // Simple client-side validation for now
                      // Check for duplicates within the input itself
                      const uniqueAlts = new Set(alternatives);
                      if (uniqueAlts.size !== alternatives.length) {
                        setAlternativeCodesError('Se encontraron códigos duplicados en la lista');
                      }
                    }}
                    value={alternativeCodesInput}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'La categoría es obligatoria' }}
                    render={({ field: { value, onChange } }) => (
                      <Autocomplete
                        freeSolo
                        options={availableCategories}
                        value={value || ''}
                        onChange={(_, newValue) => {
                          const selectedValue = newValue || '';
                          onChange(selectedValue);
                          
                          // Add new category to available list if it doesn't exist
                          if (selectedValue && 
                              !availableCategories.includes(selectedValue) && 
                              selectedValue.length > 2) {
                            setAvailableCategories(prev => [...prev, selectedValue].sort());
                          }
                        }}
                        onInputChange={(_, newInputValue) => {
                          // Only update form value when input changes (for freeSolo mode)
                          onChange(newInputValue || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Categoría"
                            error={!!errors.category}
                            helperText={errors.category?.message || 'Selecciona una existente o escribe una nueva'}
                            placeholder="Ej: CONTENEDORES, PLATOS, VASOS..."
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('subcategory')}
                    fullWidth
                    label="Subcategoría"
                    placeholder="Opcional"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    {...register('description')}
                    fullWidth
                    label="Descripción (Opcional)"
                    multiline
                    rows={3}
                    placeholder="Descripción detallada del producto"
                  />
                </Grid>

                {/* Precios */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    {...register('costPrice', { 
                      required: 'El precio de costo es obligatorio',
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor a 0' }
                    })}
                    fullWidth
                    label="Precio de Costo"
                    type="number"
                    error={!!errors.costPrice}
                    helperText={errors.costPrice?.message}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    {...register('salePrice', { 
                      required: 'El precio de venta es obligatorio',
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor a 0' }
                    })}
                    fullWidth
                    label="Precio de Venta"
                    type="number"
                    error={!!errors.salePrice}
                    helperText={errors.salePrice?.message}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                {/* Discount Price Section */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="h6" color="primary">
                      Precios con Descuento por Nivel de Cliente
                    </Typography>
                  </Divider>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    {...register('discountPrice1', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Precio Nivel 1"
                    type="number"
                    error={!!errors.discountPrice1}
                    helperText={errors.discountPrice1?.message || 'Precio para clientes de descuento nivel 1'}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    {...register('discountPrice2', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Precio Nivel 2"
                    type="number"
                    error={!!errors.discountPrice2}
                    helperText={errors.discountPrice2?.message || 'Precio para clientes de descuento nivel 2'}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    {...register('discountPrice3', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Precio Nivel 3"
                    type="number"
                    error={!!errors.discountPrice3}
                    helperText={errors.discountPrice3?.message || 'Precio para clientes de descuento nivel 3'}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TextField
                    {...register('discountPrice4', { 
                      valueAsNumber: true,
                      min: { value: 0, message: 'El precio debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Precio Nivel 4"
                    type="number"
                    error={!!errors.discountPrice4}
                    helperText={errors.discountPrice4?.message || 'Precio para clientes de descuento nivel 4'}
                    InputProps={{
                      startAdornment: 'L. ',
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    {...register('minStock', { 
                      required: 'El stock mínimo es obligatorio',
                      valueAsNumber: true,
                      min: { value: 0, message: 'El stock debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Stock Mínimo"
                    type="number"
                    error={!!errors.minStock}
                    helperText={errors.minStock?.message}
                  />
                </Grid>

                {/* Unidad */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Unidad de Medida</InputLabel>
                    <Select
                      {...register('unit', { required: 'La unidad es obligatoria' })}
                      label="Unidad de Medida"
                      error={!!errors.unit}
                      defaultValue="UDS"
                    >
                      <MenuItem value="UDS">Unidades</MenuItem>
                      <MenuItem value="KG">Kilogramos</MenuItem>
                      <MenuItem value="LB">Libras</MenuItem>
                      <MenuItem value="L">Litros</MenuItem>
                      <MenuItem value="M">Metros</MenuItem>
                      <MenuItem value="M2">Metros cuadrados</MenuItem>
                      <MenuItem value="CAJA">Caja</MenuItem>
                      <MenuItem value="PAQ">Paquete</MenuItem>
                    </Select>
                    {errors.unit && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.unit.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Ubicación inicial */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Ubicación</InputLabel>
                    <Select
                      {...register('locationId', { required: 'La ubicación es obligatoria' })}
                      label="Ubicación"
                      error={!!errors.locationId}
                      defaultValue={defaultLocation?.id || ''}
                    >
                      {locations.map(location => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.locationId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                        {errors.locationId.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Cantidad inicial */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    {...register('initialQuantity', { 
                      required: 'La cantidad inicial es obligatoria',
                      valueAsNumber: true,
                      min: { value: 0, message: 'La cantidad debe ser mayor o igual a 0' }
                    })}
                    fullWidth
                    label="Cantidad Inicial"
                    type="number"
                    error={!!errors.initialQuantity}
                    helperText={errors.initialQuantity?.message}
                    placeholder="Ej: 100"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={creatingProduct || updatingProduct}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={creatingProduct || updatingProduct || !!alternativeCodesError}
              startIcon={creatingProduct || updatingProduct ? <CircularProgress size={20} /> : null}
            >
              {creatingProduct || updatingProduct 
                ? (editingProduct ? 'Actualizando...' : 'Creando...') 
                : (editingProduct ? 'Actualizar' : 'Crear')
              }
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;